// ============================================================================
// API SERVICE - Axios Instance with JWT Authentication
// Handles API requests with automatic token attachment and refresh
// ============================================================================

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Token storage - use a mutable object to avoid closure issues
const authState = {
  accessToken: null as string | null,
  refreshTokenFn: null as (() => Promise<void>) | null,
  logoutFn: null as (() => void) | null
};

/**
 * Set the access token directly
 * Called by AuthContext when token changes
 */
export const setAccessToken = (token: string | null) => {
  authState.accessToken = token;
  // Token updated in API service
};

/**
 * Set auth functions for token refresh and logout
 * Called by AuthContext on initialization
 */
export const setAuthFunctions = (
  refresh: () => Promise<void>,
  logout: () => void
) => {
  authState.refreshTokenFn = refresh;
  authState.logoutFn = logout;
};

// Request interceptor - add token to all requests
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = authState.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      // Auth token attached
    } else {
      console.warn('No auth token available for request:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 with token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: Error | null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request while token is being refreshed
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await authState.refreshTokenFn?.();
        processQueue(null);
        isRefreshing = false;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error);
        isRefreshing = false;
        authState.logoutFn?.();
        return Promise.reject(refreshError);
      }
    }

    // Show toast for server errors and network failures
    // Suppress toasts for ambient/background endpoints that fail silently
    const silentPaths = ['/mind-bubble'];
    const isSilent = silentPaths.some(p => originalRequest?.url?.includes(p));
    if (!isSilent && error.response?.status && error.response.status >= 500) {
      toast.error('Something went wrong. Please try again.');
    } else if (!error.response) {
      toast.error('Network error. Please check your connection.');
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// API ENDPOINTS
// ============================================================================

export const sessionAPI = {
  createSession: async (data: any) => {
    const response = await api.post('/api/sessions', data);
    return response.data;
  },

  getSession: async (sessionId: string) => {
    const response = await api.get(`/api/sessions/${sessionId}`);
    return response.data;
  },

  transitionPhase: async (sessionId: string, phase: string) => {
    const response = await api.post(`/api/sessions/${sessionId}/phase`, { phase });
    return response.data;
  },

  getSessionProjects: async (sessionId: string) => {
    const response = await api.get(`/api/sessions/${sessionId}/projects`);
    return response.data;
  }
};

// Phase 3: Projects API
export const projectsAPI = {
  listProjects: async () => {
    const response = await api.get('/api/projects');
    return response.data;
  },

  getProject: async (projectId: string) => {
    const response = await api.get(`/api/projects/${projectId}`);
    return response.data;
  },

  getSessions: async (projectId: string) => {
    const response = await api.get(`/api/projects/${projectId}/sessions`);
    return response.data;
  }
};
